import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getCourses } from '../services/courseService';
import { useAuth } from './AuthContext';
import { useCacheInvalidation } from './CacheInvalidationContext';

const CourseContext = createContext();

export const useCourses = () => {
    const context = useContext(CourseContext);
    if (!context)
    {
        throw new Error('useCourses must be used within a CourseProvider');
    }
    return context;
};

export const CourseProvider = ({ children }) => {
    const { center, hasPermission } = useAuth();
    const { registerRefresh } = useCacheInvalidation();
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchCourses = useCallback(async () => {
        if (!center)
        {
            setLoading(false);
            return;
        }

        // Skip fetch if user doesn't have permission
        if (!hasPermission('courses:list')) {
            setLoading(false);
            setError(null);
            setCourses([]);
            return;
        }

        try
        {
            setLoading(true);
            setError(null);
            const data = await getCourses();
            setCourses(data);
        } catch (err)
        {
            setError(err);
            setCourses([]);
        } finally
        {
            setLoading(false);
        }
    }, [center, hasPermission]);

    // Fetch courses when center changes
    useEffect(() => {
        fetchCourses();
    }, [fetchCourses]);

    // Poll for updates every 10 minutes (when page is visible)
    useEffect(() => {
        const pollInterval = setInterval(() => {
            if (document.visibilityState === 'visible') {
                fetchCourses();
            }
        }, 10 * 60 * 1000); // 10 minutes

        return () => clearInterval(pollInterval);
    }, [fetchCourses]);

    // Refresh function to be called after CRUD operations
    const refreshCourses = useCallback(() => {
        fetchCourses();
    }, [fetchCourses]);

    // Register refresh function for cache invalidation
    useEffect(() => {
        const cleanup = registerRefresh('courses', refreshCourses);
        return cleanup;
    }, [registerRefresh, refreshCourses]);

    const value = {
        courses,
        loading,
        error,
        refreshCourses,
        fetchCourses
    };

    return (
        <CourseContext.Provider value={value}>
            {children}
        </CourseContext.Provider>
    );
};

export default CourseContext;
